/* Read-only theme snapshot adapter for the public vehicle lookup endpoints. */
(() => {
  'use strict';
  const configNode = document.getElementById('ceika-vehicle-data-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent || '{}'); } catch (_) { return; }
  const indexURLValue = config.indexURL || config.indexUrl;
  if (!indexURLValue) return;
  let indexURL;
  try { indexURL = new URL(indexURLValue, location.href).href; } catch (_) { return; }
  const indexPromise = fetchJSON(indexURL);
  const makeCache = new Map();
  const productCache = new Map();
  const overlayLookupCache = new Map();
  const aliases = {brakes:'bbk', airride:'air_ride', wheels:'forged_wheels'};
  const FAMILY_NAMES = {coilovers:'coilovers',bbk:'brakes',air_ride:'airride',forged_wheels:'wheels'};
  const familyKeys = ['coilovers','bbk','air_ride','forged_wheels'];
  const identityReviewHolds = new Set(['cbk524','ceika-custom-big-brake-kit-for-mazda-cx-50-va-22-up','ceika-custom-big-brake-kit-for-dodge-challenger-hellcat-14-up']);
  const vehicleMatchAllowed = ref => {
    if(ref.family !== 'bbk') return true;
    if(ref.handle === 'ceika-custom-big-brake-kit-for-mazda-cx-50-va-22-up') return ref.mode === 'vehicle' && ref.vehicleId === '8400b70d-b32e-568a-8027-625dddce4160' && ref.yearStart === 2022 && ref.yearEnd === 9999;
    return !identityReviewHolds.has(ref.handle);
  };
  function abortError(signal) { if (signal?.aborted) { const e = new Error('aborted'); e.name = 'AbortError'; throw e; } }
  async function fetchJSON(url, signal) {
    abortError(signal);
    const response = await fetch(url, {signal, credentials:'omit', headers:{Accept:'application/json'}});
    if (!response.ok) { const error = new Error(response.status === 404 ? 'unknown_vehicle' : 'snapshot_unavailable'); error.status = response.status; throw error; }
    return response.json();
  }
  function assetURL(filename, indexUrl) { return new URL(filename, indexUrl).href; }
  async function index(signal) { return indexPromise; }
  async function loadMake(make, signal) {
    abortError(signal);
    if (!makeCache.has(make)) makeCache.set(make, index().then(i => {
      const file = i.makeShards?.[make];
      if (!file) return null;
      return fetchJSON(assetURL(file, indexURL), signal);
    }));
    return makeCache.get(make);
  }
  async function loadProductChunk(family, chunk, signal) {
    abortError(signal);
    const key = `${family}:${chunk}`;
    if (!productCache.has(key)) productCache.set(key, index().then(i => {
      const file = i.productShards?.[family]?.[chunk];
      return file ? fetchJSON(assetURL(file, indexURL), signal) : {products:[]};
    }));
    return productCache.get(key);
  }
  function readScripts(selector) {
    const nodes = document.querySelectorAll?.(selector) || [];
    const values = [];
    for (const node of nodes) {
      try { values.push(JSON.parse(node.textContent || 'null')); } catch (_) {}
    }
    return values;
  }
  function validString(value, max=160) { return typeof value === 'string' && value.length > 0 && value.length <= max; }
  function validYear(value) { return Number.isInteger(value) && value >= 1886 && value <= 9999; }
  function validRules(value) {
    return Array.isArray(value) && value.every(x=>typeof x==='string'&&x.length<=160);
  }
  function validAssignment(value, allowUnassigned=false) {
    const unassigned=allowUnassigned&&value?.vehicle_id==null;
    if (!value || (!unassigned&&!validString(value.vehicle_id,100)) ||
        (unassigned ? (value.year_start!=null||value.year_end!=null) : (!validYear(value.year_start)||!validYear(value.year_end)||value.year_start>value.year_end))) return null;
    if (!validString(value.decision,80) || !validString(value.custom_fit_state,80) || !validRules(value.include_collections) || !validRules(value.exclude_collections)) return null;
    return {vehicleId:unassigned?null:value.vehicle_id,yearStart:value.year_start,yearEnd:value.year_end,customFit:value.custom_fit_state==='custom_fit_required',
      includeCollections:value.include_collections,excludeCollections:value.exclude_collections};
  }
  function collectOverlayProducts() {
    const records=[];
    for (const doc of readScripts('[data-ceika-vehicle-overlay]')) {
      if (!doc || doc.schemaVersion!==1 || !doc.products || typeof doc.products!=='object') continue;
      const entries=Array.isArray(doc.products)?doc.products:Object.values(doc.products);
      for (const value of entries) {
        if (!value || !validString(value.productId,180) || !validString(value.handle,180) || !/^[a-z0-9][a-z0-9_-]{0,179}$/.test(value.handle) ||
            value.productId!==value.handle || !Number.isInteger(value.version) || value.version<1 || typeof value.visible!=='boolean') continue;
        const mode=value.match_mode;
        if (!['vehicle','custom','hidden'].includes(mode)) continue;
        if (mode==='vehicle' && !Array.isArray(value.assignments)) continue;
        const assignments=(Array.isArray(value.assignments)?value.assignments:[]).map(a=>validAssignment(a,mode==='custom')).filter(Boolean);
        records.push({productId:value.productId,handle:value.handle,version:value.version,visible:value.visible,mode,assignments});
      }
    }
    const byHandle=new Map(),ambiguous=new Set();
    for (const record of records) {
      const prior=byHandle.get(record.handle);
      if (prior && JSON.stringify(prior)!==JSON.stringify(record)) ambiguous.add(record.handle);
      else byHandle.set(record.handle,record);
    }
    for (const handle of ambiguous) byHandle.delete(handle);
    return byHandle;
  }
  function collectCustomVehicles() {
    const records=[];
    for (const value of [...readScripts('[data-ceika-vehicle-custom-vehicles]'),...readScripts('[data-ceika-custom-vehicles]')]) {
      const entries=Array.isArray(value)?value:(Array.isArray(value?.vehicles)?value.vehicles:[]);
      for (const v of entries) {
        if (!v || !validString(v.id,100) || !validString(v.make,100) || !validString(v.model,140) || (v.variant!=null&&typeof v.variant!=='string') ||
            !validYear(v.start_year) || !validYear(v.end_year) || v.start_year>v.end_year || v.status!=='provisional') continue;
        records.push({id:v.id,make:v.make,model:v.model,variant:v.variant||'',startYear:v.start_year,endYear:v.end_year,status:'provisional'});
      }
    }
    const map=new Map(),ambiguous=new Set();
    for (const v of records) {
      const key=shortId(v.id), prior=map.get(key);
      if (prior && JSON.stringify(prior)!==JSON.stringify(v)) ambiguous.add(key);
      else map.set(key,v);
    }
    for (const id of ambiguous) map.delete(id);
    return [...map.values()];
  }
  function overlayBucket(handle) {
    let hash=2166136261;
    for(let i=0;i<handle.length;i++) hash=Math.imul(hash^handle.charCodeAt(i),16777619);
    return (hash>>>0&15).toString(16);
  }
  async function loadOverlayLookup(idx, handles, signal) {
    const buckets=[...new Set(handles.map(overlayBucket))];
    await Promise.all(buckets.map(async bucket=>{
      if (overlayLookupCache.has(bucket)) return;
      const file=idx.overlayLookup?.[bucket];
      if (!file) { overlayLookupCache.set(bucket,new Map()); return; }
      const data=await fetchJSON(assetURL(file,indexURL),signal), map=new Map();
      for (const row of data.products || []) {
        if (!Array.isArray(row)||!validString(row[0],180)||!Number.isInteger(row[1])||!Number.isInteger(row[2])) continue;
        const family=idx.overlayLookupFamilies?.[row[1]];
        if (family) map.set(row[0],{family,shard:row[2]});
      }
      overlayLookupCache.set(bucket,map);
    }));
    const result=new Map();
    for (const handle of handles) result.set(handle,overlayLookupCache.get(overlayBucket(handle))?.get(handle));
    return result;
  }
  async function getOverlay(idx, signal) {
    const products=collectOverlayProducts(),customVehicles=collectCustomVehicles();
    let byHandle=new Map();
    if (products.size) {
      const lookup=await loadOverlayLookup(idx,[...products.keys()],signal);
      for (const [handle,value] of products) {
        const ref=lookup.get(handle);
        if (ref) byHandle.set(handle,{...value,...ref,hidden:value.visible===false||value.mode==='hidden'});
      }
    }
    return {byHandle,customVehicles};
  }
  function overlayCollectionRules(record) {
    const includes=[...new Set((record?.assignments||[]).flatMap(a=>a.includeCollections||[]))];
    const excludes=[...new Set((record?.assignments||[]).flatMap(a=>a.excludeCollections||[]))];
    return {includeCollections:includes,excludeCollections:excludes};
  }
  function withCustomVehicles(idx, customVehicles) {
    if (!customVehicles.length) return idx;
    const makeNames=[...idx.makeNames],makeNo=new Map(makeNames.map((x,i)=>[x,i]));
    const existing=new Set(idx.vehicleIndex.map(row=>row[0])),rows=[...idx.vehicleIndex],customVehicleMap=new Map();
    for (const v of customVehicles) {
      const id=shortId(v.id); if (existing.has(id)) continue;
      if (!makeNo.has(v.make)) { makeNo.set(v.make,makeNames.length); makeNames.push(v.make); }
      rows.push([id,makeNo.get(v.make),v.model,v.variant,v.startYear,v.endYear]); existing.add(id); customVehicleMap.set(id,v);
    }
    return {...idx,makeNames,vehicleIndex:rows,customVehicleMap};
  }
  const s = value => String(value == null ? '' : value);
  const shortId = value => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s(value)) ? s(value).replace(/-/g,'').toLowerCase() : s(value);
  const fullId = value => /^[0-9a-f]{32}$/i.test(value) ? `${value.slice(0,8)}-${value.slice(8,12)}-${value.slice(12,16)}-${value.slice(16,20)}-${value.slice(20)}` : value;
  const norm = value => s(value).toLocaleLowerCase();
  const words = value => s(value).trim().split(/\s+/).filter(Boolean);
  function yearToken(word) {
    if (/^\d{4}$/.test(word)) return Number(word);
    if (/^\d{2}$/.test(word)) return Number(word) < 30 ? 2000 + Number(word) : 1900 + Number(word);
    return null;
  }
  function matches(v, query, maxYear) {
    return words(query).every(word => {
      const token = norm(word);
      const y = yearToken(word);
      const names = [v.make, v.model, v.variant].map(norm).join(' ');
      // Numeric tokens can name a model (Audi 90, BMW 2002TI), but 90 alone is not E90.
      const nameMatch = y === null ? names.includes(token) : new RegExp(`(^|[^a-z0-9])${token}(?!\\d)`).test(names);
      return nameMatch || (y !== null && v.start_year <= y && v.end_year >= y && y <= maxYear);
    });
  }
  function vehicleFromRow(row, indexData) {
    const [id, makeNo, model, variant, startYear, endYear] = row;
    const make = indexData.makeNames[makeNo];
    const displayEndYear = endYear === 9999 ? 'up' : endYear;
    const yearRange = {start:startYear, end:endYear === 9999 ? null : endYear, openEnded:endYear === 9999};
    const label = `${startYear}${endYear !== startYear ? `–${displayEndYear}` : ''} ${make} ${model} ${variant}`.trim();
    return {id:fullId(id),make,model,variant,start_year:startYear,end_year:endYear,year:startYear,displayEndYear,yearRange,label};
  }
  function exactYear(params, indexData) {
    if (!Object.hasOwn(params,'year')) return null;
    const raw = s(params.year);
    const year = Number(raw);
    if (!/^\d{4}$/.test(raw) || year < indexData.yearBounds.min || year > indexData.yearBounds.max) throw new Error('invalid_year');
    return year;
  }
  function page(items, params, indexData) {
    const limit = Math.min(100, Math.max(1, Number(params.limit ?? 50) || 50));
    const offsetRaw = params.cursor == null || params.cursor === '' ? '0' : s(params.cursor);
    const offset = /^\d+$/.test(offsetRaw) ? Number(offsetRaw) : 0;
    const rows = items.slice(offset, offset + limit + 1);
    const hasMore = rows.length > limit;
    return {items:rows.slice(0,limit), nextCursor:hasMore ? String(offset + limit) : null, hasMore, dataVersion:indexData.dataVersion};
  }
  function collectionAllowed(rules, collection) {
    if (!collection) return true;
    const includes = rules?.includeCollections || [];
    const excludes = rules?.excludeCollections || [];
    return !excludes.includes(collection) && (!includes.length || includes.includes(collection));
  }
  function productFamily(family) { return aliases[family] || family || null; }
  function outputProduct(p, locale, fitmentState) {
    const prefix = locale && locale !== 'en' ? `/${s(locale).replace(/^\/+|\/+$/g,'')}` : '';
    return {handle:p.handle,title:p.title,description:p.description,image:p.image || '',url:prefix + p.path,
      family:p.family,price:null,fitmentState,dataConfidence:'snapshot_provisional'};
  }
  async function productRecords(refs, signal) {
    const grouped = new Map();
    for (const ref of refs) {
      const family = ref.family;
      if (!grouped.has(family)) grouped.set(family, new Set());
      grouped.get(family).add(Number(ref.shard));
    }
    const result = new Map();
    for (const [family, chunks] of grouped) {
      const data = await Promise.all([...chunks].map(chunk => loadProductChunk(family,chunk,signal)));
      for (const shard of data) for (const p of (shard.products || [])) result.set(p.handle,p);
    }
    return result;
  }
  async function vehicleEndpoint(params, signal) {
    const base = await index(signal), overlay = await getOverlay(base,signal), idx = withCustomVehicles(base,overlay.customVehicles);
    const level = params.level || 'vehicles', q = s(params.q), make = s(params.make), model = s(params.model);
    const year = exactYear(params, idx);
    if (level === 'years') {
      let rows = idx.vehicleIndex.map(r => vehicleFromRow(r,idx)).filter(v => v.start_year <= idx.yearBounds.max && v.end_year >= idx.yearBounds.min && (!make || v.make === make) && (!model || v.model === model));
      const found = new Set(); for (const v of rows) for (let y=Math.max(idx.yearBounds.min,v.start_year); y<=Math.min(idx.yearBounds.max,v.end_year); y++) found.add(y);
      const term = q.trim(); let years = [...found].sort((a,b)=>b-a);
      if (term) years = years.filter(y=>String(y).startsWith(term));
      return page(years.map(y=>({year:y,label:String(y)})),params,idx);
    }
    let rows = idx.vehicleIndex.map(r => vehicleFromRow(r,idx));
    if (year !== null) rows = rows.filter(v=>v.start_year<=year && v.end_year>=year);
    if (make) rows = rows.filter(v=>v.make === make);
    if (model && level !== 'models') rows = rows.filter(v=>v.model === model);
    rows = rows.filter(v=>matches(v,q,idx.yearBounds.max));
    rows.sort((a,b)=>a.make.localeCompare(b.make)||a.model.localeCompare(b.model)||a.variant.localeCompare(b.variant)||a.start_year-b.start_year);
    if (level === 'makes') {
      const unique = [...new Set(rows.map(v=>v.make))].sort((a,b)=>a.localeCompare(b)).map(make=>({make,label:make}));
      return page(unique,params,idx);
    }
    if (level === 'models') {
      let filtered = rows.filter(v=>v.make===make);
      const groups = new Map();
      for (const v of filtered) groups.set(v.model,true);
      const eligible = idx.vehicleIndex.map(r=>vehicleFromRow(r,idx)).filter(v=>v.make===make&&(year===null||(v.start_year<=year&&v.end_year>=year)));
      const models = [...groups.keys()].sort((a,b)=>a.localeCompare(b)).map(name=>({make,model:name,label:name,
        searchAliases:[...new Set(eligible.filter(v=>v.model===name).map(v=>v.variant).filter(Boolean))].sort((a,b)=>a.localeCompare(b))}));
      return page(models,params,idx);
    }
    if (level === 'variants') {
      const variants = [...new Set(rows.filter(v=>v.make===make&&v.model===model).map(v=>v.variant))].sort((a,b)=>a.localeCompare(b)).map(variant=>({make,model,variant,label:variant||'Standard'}));
      return page(variants,params,idx);
    }
    return page(rows,params,idx);
  }
  async function recordsForVehicle(vehicle, family, collection, year, signal, overlay, idx) {
    const makeData = await loadMake(vehicle.make,signal);
    let refs = (makeData?.applications?.[shortId(vehicle.id)] || []).filter(a => !family || a.family === family);
    const out=[];
    for (const ref of refs) {
      const saved=overlay.byHandle.get(ref.handle);
      if (saved) continue; // A saved product record replaces this product's snapshot assignments.
      if (year!==null&&(ref.yearStart>year||ref.yearEnd<year)) continue;
      if (!collectionAllowed(ref,collection)) continue;
      out.push({...ref,mode:'vehicle'});
    }
    for (const [handle,saved] of overlay.byHandle) {
      if (saved.hidden||saved.mode!=='vehicle'||(family&&saved.family!==family)) continue;
      for (const assignment of saved.assignments) {
        if (assignment.vehicleId!==vehicle.id || (year!==null&&(assignment.yearStart>year||assignment.yearEnd<year))) continue;
        if (!collectionAllowed(assignment,collection)) continue;
        out.push({handle,vehicleId:assignment.vehicleId,family:saved.family,shard:saved.shard,yearStart:assignment.yearStart,yearEnd:assignment.yearEnd,
          customFit:assignment.customFit,includeCollections:assignment.includeCollections,excludeCollections:assignment.excludeCollections,mode:'vehicle'});
      }
    }
    return out.filter(vehicleMatchAllowed);
  }
  function customRefsForFamily(idx, overlay, family, collection) {
    const refs=[];
    for (const ref of idx.customHandles?.[family]||[]) {
      const saved=overlay.byHandle.get(ref.handle);
      if (saved) {
        if (!saved.hidden&&saved.mode==='custom'&&collectionAllowed(overlayCollectionRules(saved),collection)) refs.push({...saved,customFit:true});
      } else if (collectionAllowed(ref,collection)) refs.push({...ref,mode:'custom',customFit:true});
    }
    for (const [handle,saved] of overlay.byHandle) {
      if (saved.hidden||saved.mode!=='custom'||saved.family!==family||idx.customHandles?.[family]?.some(x=>x.handle===handle)) continue;
      if (collectionAllowed(overlayCollectionRules(saved),collection)) refs.push({...saved,customFit:true});
    }
    return refs.filter(vehicleMatchAllowed);
  }
  async function availabilityEndpoint(params, signal) {
    const base = await index(signal), overlay = await getOverlay(base,signal), idx = withCustomVehicles(base,overlay.customVehicles);
    const vehicleId = s(params.vehicle_id ?? params.vehicleId), compactVehicleId = shortId(vehicleId), row = idx.vehicleIndex.find(r=>r[0]===compactVehicleId), makeNo = row?.[1];
    if (makeNo === undefined) throw new Error('unknown_vehicle');
    const vehicleRaw = vehicleFromRow(row,idx), make = vehicleRaw.make;
    const vehicle = {id:vehicleId,make,model:vehicleRaw.model,variant:vehicleRaw.variant,startYear:vehicleRaw.start_year,endYear:vehicleRaw.end_year};
    const year = exactYear(params,idx);
    if (year !== null && (year < vehicle.startYear || year > vehicle.endYear)) throw new Error('year_out_of_range');
    const collection = s(params.collection), locale = s(params.locale || 'en');
    const groups = [];
    for (const fam of familyKeys) {
      const refs = await recordsForVehicle(vehicle,fam,collection,year,signal,overlay,idx);
      refs.push(...customRefsForFamily(idx,overlay,fam,collection));
      const state = refs.length && refs.every(r=>r.mode==='custom') ? 'custom' : 'unknown';
      groups.push({key:FAMILY_NAMES[fam],state,matchedProductCount:refs.length,productTitle:null,
        summary:null,price:null,priceEvidence:{amountMinor:null,currency:null,source:null,usable:false},url:null,products:[]});
    }
    const end = vehicle.endYear===9999?'up':vehicle.endYear;
    const rangeLabel = `${vehicle.startYear}–${end} ${vehicle.make} ${vehicle.model} ${vehicle.variant}`.trim();
    const label = year===null ? rangeLabel : `${year} ${vehicle.make} ${vehicle.model} ${vehicle.variant}`.trim();
    return {schemaVersion:1,vehicle:{id:vehicleId,label,...(year===null?{}:{year,applicationLabel:rangeLabel})},
      context:{country:s(params.country),currency:s(params.currency),locale:locale||'en'},groups,
      generatedAt:idx.generatedAt,dataVersion:idx.dataVersion};
  }
  async function productsEndpoint(params, signal) {
    const base = await index(signal), overlay = await getOverlay(base,signal), idx = withCustomVehicles(base,overlay.customVehicles);
    const vehicleId = s(params.vehicle_id ?? params.vehicleId), all = s(params.browse_all)==='1';
    const year = exactYear(params,idx), collection = s(params.collection), locale = s(params.locale || 'en'), family = productFamily(params.family), term = norm(params.q);
    let vehicle = null, refs = [];
    if (vehicleId && !all) {
      const compactVehicleId = shortId(vehicleId), makeNo = idx.vehicleIndex.find(r=>r[0]===compactVehicleId)?.[1];
      if (makeNo===undefined) throw new Error('unknown_vehicle');
      const raw = vehicleFromRow(idx.vehicleIndex.find(r=>r[0]===compactVehicleId),idx);
      if (year!==null&&(year<raw.start_year||year>raw.end_year)) throw new Error('year_out_of_range');
      vehicle = {id:vehicleId,make:raw.make,model:raw.model,variant:raw.variant,startYear:raw.start_year,endYear:raw.end_year};
      refs = await recordsForVehicle(vehicle,family,collection,year,signal,overlay,idx);
      // Generic custom-fit listings are visible for any selected vehicle.
      for (const fam of Object.keys(idx.productShards||{})) if (!family||family===fam) refs.push(...customRefsForFamily(idx,overlay,fam,collection));
    } else {
      for (const [fam, entries] of Object.entries(idx.productShards || {})) {
        if (family && family!==fam) continue;
        for (let chunk=0;chunk<entries.length;chunk++) {
          const shard = await loadProductChunk(fam,chunk,signal);
          for (const p of shard.products || []) {
            const saved=overlay.byHandle.get(p.handle);
            if (saved?.hidden) continue;
            refs.push({handle:p.handle,family:fam,shard:chunk,customFit:saved?saved.mode==='custom':p.matchMode==='custom',mode:saved?.mode||p.matchMode});
          }
        }
      }
    }
    const map = await productRecords(refs,signal);
    const unique = new Map();
    for (const ref of refs) {
      const p = map.get(ref.handle); if (!p) continue;
      const saved=overlay.byHandle.get(ref.handle);
      if (!vehicle && !collectionAllowed(saved?overlayCollectionRules(saved):p,collection)) continue;
      if (term && !`${p.title} ${p.handle}`.toLocaleLowerCase().includes(term)) continue;
      if (!unique.has(p.handle)) unique.set(p.handle,outputProduct(p,locale,(ref.customFit||ref.mode==='custom'||p.matchMode==='custom')?'custom':'unknown'));
    }
    const allProducts = [...unique.values()].sort((a,b)=>a.title.localeCompare(b.title));
    const requestedSort = s(params.sort || 'title');
    const offset = Math.max(0,Number.parseInt(params.offset || '0',10)||0), limit = Math.min(100,Math.max(1,Number.parseInt(params.limit || '24',10)||24));
    const result = allProducts.slice(offset,offset+limit);
    return {total:allProducts.length,offset,limit,requestedSort,sort:'title',sortUnavailable:['price_asc','price_desc','price-ascending','price-descending'].includes(requestedSort),
      next_offset:offset+limit<allProducts.length?offset+limit:null,products:result,dataVersion:idx.dataVersion};
  }
  async function request(path, params={}, signal) {
    abortError(signal);
    const pathname = new URL(path, location.origin).pathname;
    if (pathname === '/api/v1/vehicles') return vehicleEndpoint(params,signal);
    if (pathname === '/api/v1/availability') return availabilityEndpoint(params,signal);
    if (pathname === '/api/v1/products') return productsEndpoint(params,signal);
    throw new Error('not_found');
  }
  Object.defineProperty(globalThis,'CeikaVehicleData',{value:Object.freeze({request}),configurable:false});
})();


